import { lazy } from 'react';
import styles from '../styles/Battlefield.module.css';
import LazyWhenShown from './LazyWhenShown.jsx';
import BattlefieldOptionBar from './BattlefieldOptionBar';
import BattlefieldRoomRail from './BattlefieldRoomRail';
import StageTitleCard from './StageTitleCard';
import { curtainHides } from '../engine/scenes';
import { withPanelDefaults } from './battlefieldPanel/panelDefaults';
import useLastTurn from '../hooks/useLastTurn';
import { useBattlefieldPanel } from './battlefieldPanel/useBattlefieldPanel';
import { viewOptionsOf } from './battlefieldPanel/viewOptions';
import PanelMapColumn from './battlefieldPanel/PanelMapColumn';
import PanelScenes from './battlefieldPanel/PanelScenes';
import PanelToolbar from './battlefieldPanel/PanelToolbar';

// Opened by a GM building, rarely: loaded on first use (the perf overhaul).
const TerrainEditorModal = lazy(() => import('./TerrainEditorModal'));

/**
 * The GM's waiting rooms, beside the map in Deploy. REPLACE lives in the
 * rail, not the bar: the bar is two wrapped rows at a narrow centre column
 * already, and one more control pushed it into the map (v8.344.2).
 */
function PanelRoomRail({ panel, map }) {
  const { onReplacePick } = panel;
  return (
    <BattlefieldRoomRail
      rooms={panel.waitingRooms}
      activeId={panel.activeRoomId}
      onActive={panel.onActiveRoom}
      onDeployRoom={panel.onDeployRoom}
      onNewRoom={panel.onNewRoom}
      onDismiss={panel.onDismiss}
      onDeleteRoom={panel.onDeleteRoom}
      onMoveToRoom={panel.onMoveToRoom}
      disabled={!panel.deployment}
      onReplace={onReplacePick ? () => map.setReplaceArmed((v) => !v) : undefined}
      replaceArmed={map.replaceArmed}
    />
  );
}

/**
 * BattlefieldPanel - the tactical map, with the combat log floating over
 * its upper portion.
 *
 * The panel owns the camera, the toolbar and the drag gestures; the scene
 * owns nothing but drawing. Terrain and tokens arrive as plain records, so
 * this component has no opinion about whether they came from Firestore or
 * from local state. Its hooks are `useBattlefieldPanel`; its furniture is
 * in `battlefieldPanel/`; what an absent prop means is `panelDefaults`.
 *
 * Callbacks with no default (absent = that feature has no session behind it):
 * `onPing`; `onMove` (somebody climbed or dropped more than a stride);
 * `onMoveMade` (a move across the ground finished ON THIS CLIENT: its facts
 * for the log's public line, plan design-gaps M2);
 * `onTokenSettled` (a marker finished a move ON THIS CLIENT - the overwatch
 * check); the waiting rooms' `onActiveRoom`, `onDeployRoom`, `onNewRoom`,
 * `onDismiss`, `onDeleteRoom`, `onMoveToRoom`, `onRecall`, `onRecallMany`,
 * `onDeployMember`; `onReplaceMember(memberId, outgoingId)` - a room member
 * dropped ON somebody replaces them (v8.343.0); `onReplacePick(outgoingId)`
 * - the armed REPLACE and a marker clicked (v8.344.0); `onStampTeam` - the
 * deploy marker's team pass; `areaEligible` / `areaSpawnEligible` - the
 * template's catches as predicates over combatant ids (the secondary's is
 * strict, nobody exempt, v8.373.5); `onBuildingChange` - told whenever the
 * map wants or gives back the whole column, so the shell can decide
 * whether the combat log yields (v8.0.1). `modes` - which modes are on
 * offer, absent meaning all: the map workshop authors terrain with no
 * roster, and a mode that cannot work is worse than one not offered
 * (v8.2.0).
 */
export default function BattlefieldPanel(props) {
  const panel = usePanelProps(props);
  const map = useBattlefieldPanel(panel);
  const view = viewOptionsOf(map.prefs, panel, map.cam);
  const { build, boundary } = map;
  // THE STAGE: the map, and everything that floats over it. Nothing in it
  // may be in FLOW - anything that takes vertical space when it appears
  // resizes the SVG, which re-projects the whole scene and moves whatever
  // was under the pointer. The OPTION BAR runs across its top, Build only
  // (v8.0.0); the map area under it is the rails' reference.
  return (
    <div className={styles.panel} data-testid="battlefield-panel">
      <div className={styles.stage}>
        {map.building && (
          <BattlefieldOptionBar
            active={map.buildOption}
            onActive={map.setBuildOption}
            onOpenEditor={() => map.setTerrainEditor(true)}
            build={build}
            boundary={boundary}
            faceCount={map.solids.length * 6}
            mode={map.mode}
            view={view}
          />
        )}
        <div className={styles.stageBody}>
          <div className={styles.stageRow}>
            <PanelMapColumn panel={panel} map={map} />
            <PanelScenes panel={panel} map={map} />
            {/* Waiting rooms are a session's: where none can be made (the Assets scene editor), no rail. */}
            {map.deploying && panel.onNewRoom && <PanelRoomRail panel={panel} map={map} />}
          </div>
        </div>
        <StageTitleCard
          card={panel.stageCard}
          held={curtainHides(panel.stage, panel.isGm)}
          display={map.renderer === 'display'}
        />
        <CurtainBadge stage={panel.stage} isGm={panel.isGm} />
      </div>
      <LazyWhenShown
        component={TerrainEditorModal}
        show={map.terrainEditor}
        onClose={() => map.setTerrainEditor(false)}
        build={build}
        boundary={boundary}
        tableAssets={panel.tableAssets}
      />
      <PanelToolbar panel={panel} map={map} view={view} />
    </div>
  );
}

/**
 * One copy of the props: withPanelDefaults makes it, and the LAST TURN's replay (Z6) goes onto it - its impacts and
 * walks merged over the live ones. The camera's beat reads the LIVE impacts, never a replay (`liveImpacts`).
 */
function usePanelProps(props) {
  const lastTurn = useLastTurn(props.impacts, props.walks, props.turnActorId);
  const replay = { impacts: lastTurn.impacts, walks: lastTurn.walks, lastTurn, liveImpacts: props.impacts };
  return Object.assign(withPanelDefaults(props), replay);
}

/** The GM's reminder that the players see the curtain, not the stage. */
function CurtainBadge({ stage, isGm }) {
  if (!stage?.curtain || !isGm) return null;
  return (
    <div className={styles.curtainBadge} data-testid="stage-curtain-badge">
      CURTAIN DOWN
    </div>
  );
}
